/**
 * The case's Case Prep workflows: where each one stands, the firm defaults
 * its prompt reads, and the runner. A step's prompt is sent on its lane's
 * CaseBlink Workflow thread by the executor the chat panel registers, and
 * a lane runs one step at a time. A step starts once the steps it comes
 * after (`after`, `dependsOn`) have ended, so steps on different lanes run
 * side by side. A failed step is marked and the run goes on, skipping the
 * steps that need it; a chained step with nothing to do sits out with the
 * reason (`skips.ts`). The steps still to run are remembered per case, so
 * a refresh continues the run instead of stopping after the step in flight.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { firmKnowledgeApi } from "@/api/firm-knowledge";
import { LawFirmsAPI } from "@/api/lawFirms";
import type { LawFirm } from "@/api/types/lawFirm";
import type { FirmLibraryEntry } from "@/features/firmKnowledge/types";
import { useCaseOptional } from "@/contexts/CaseContext";
import {
  caseCustomType,
  draftingVisaOf,
  liveTypeOf,
  typeDefaultTemplates
} from "@/features/customCaseTypes/customCaseTypes";
import { FIRM_UPDATED_EVENT } from "@/features/lawFirms/firmEvents";
import { lawyerSelectors } from "@/redux/lawyer/selectors";

import { matchPreset } from "../../../caseFiles/exhibitStylePresets";
import {
  DEFAULT_EXHIBIT_SCHEME,
  type ExhibitScheme
} from "../../../caseFiles/types";
import { useCaseIde } from "../../context/CaseIdeContext";
import { useCaseData } from "../../data/CaseDataContext";
import { ideLayoutStore } from "../../layout/ideLayoutStore";
import { useCaseVisa } from "../../useCaseVisa";
import { WorkspaceTreeAPI } from "../../workspace/api/workspaceTreeApi";
import {
  draftStatus,
  type WorkspaceStatus
} from "../../workspace/workspaceModel";
import {
  REQUIRED_WORKFLOW_IDS,
  readWorkflowPrompt,
  workflowById
} from "../catalog";
import { defaultTemplatesFor } from "../defaultTemplates";
import {
  addFormPick,
  formChoicesOf,
  formTypesOf,
  removeFormPick,
  setFormPickNote,
  type FormPick,
  type FormTypes
} from "../forms";
import {
  packetArgsOf,
  packetContentsOf,
  packetOrderOf
} from "../packetContents";
import { useResearchTopics } from "../hooks/useResearchTopics";
import {
  defaultPicks,
  letterTypesOf,
  lettersFor,
  type LetterPick,
  type LetterPicks
} from "../letters";
import {
  pickedTopics,
  type ResearchTopic,
  type ResearchTopicsState,
  type TopicPicks
} from "../researchTopics";
import { skipReason } from "../skips";
import {
  caseStyleDifference,
  firmScheme,
  labelStyleOf,
  workflowSettings
} from "../settings";
import {
  doneCount,
  hasPriorWork,
  nextUpId,
  remainingIds,
  titleSettingsKey,
  titlesKey,
  workflowFacts,
  workflowViews,
  type WorkflowFacts
} from "../status";
import {
  clearQueue,
  readNotes,
  readQueue,
  readRuns,
  writeNotes,
  writeQueue,
  writeRuns,
  type StoredQueue,
  type StoredQueueItem
} from "../storage";
import type {
  FormChoice,
  FormOption,
  LetterChoice,
  LetterType,
  WorkflowExecutor,
  WorkflowId,
  WorkflowLane,
  WorkflowRunRecord,
  WorkflowScope,
  WorkflowSettings,
  WorkflowStepState,
  WorkflowTurnOutcome,
  WorkflowView
} from "../types";

export type WorkflowsState = {
  settings: WorkflowSettings;
  firmScheme: ExhibitScheme | null;
  /** The case's label style when it isn't the firm's. */
  caseLabelStyle: string | null;
  facts: WorkflowFacts;
  views: WorkflowView[];
  nextUp: WorkflowId | null;
  /** The letter types the case's visa calls for; null while loading. */
  letterTypes: LetterType[] | null;
  library: FirmLibraryEntry[];
  /** Draft Letters' choices: every type, checked, with the firm default. */
  letterPicks: Record<string, LetterPick>;
  setLetterPick: (draftType: string, pick: LetterPick) => void;
  /** The checked letters with their templates. */
  letters: LetterChoice[];
  /** Every form CaseBlink fills; null while loading. */
  formCatalog: FormOption[] | null;
  /** Fill Forms' copies, in order; null while loading or when the server can't say. */
  formPicks: FormPick[] | null;
  /** Another copy of a form, even one already picked. */
  addForm: (form: FormOption) => void;
  removeForm: (id: string) => void;
  setFormNote: (id: string, note: string) => void;
  /** The copies a Fill Forms run fills, each labelled apart. */
  forms: FormChoice[];
  /** Assemble Filing Packet: a hand pick per work product. */
  packetPicks: Record<string, boolean>;
  setPacketPick: (key: string, on: boolean) => void;
  /** Assemble Filing Packet: drag order of work-product keys. */
  setPacketOrder: (keys: string[]) => void;
  /** Case Research's topics for this case type and posture. */
  researchTopics: ResearchTopicsState;
  /** Topics checked or unchecked by hand; the rest follow `defaultPicked`. */
  topicPicks: TopicPicks;
  setTopicPicks: (ids: string[], picked: boolean) => void;
  /** The checked topics, card topics then More Topics. */
  topics: ResearchTopic[];
  remaining: WorkflowId[];
  done: number;
  /** The case has work a start-over would replace. */
  priorWork: boolean;
  /** A step is running or queued. */
  running: boolean;
  /** The first step running now, in run order. */
  current: WorkflowId | null;
  /** Every step running now (or waiting on its thread), in run order. */
  active: WorkflowId[];
  /** Every step of the current run, in order. */
  runIds: WorkflowId[];
  /** Casey steps whose prompt isn't sent yet, in send order, as the chat lists them. */
  pending: PendingWorkflow[];
  /** Take one step out of the run before its prompt is sent. */
  dequeue: (id: WorkflowId) => void;
  notes: Partial<Record<WorkflowId, string>>;
  setNote: (id: WorkflowId, note: string) => void;
  /**
   * The exact prompt a run would send now; Draft Letters writes `letters`,
   * Case Research `topics`.
   */
  promptFor: (
    id: WorkflowId,
    scope?: WorkflowScope,
    letters?: LetterChoice[],
    topics?: ResearchTopic[]
  ) => string;
  run: (
    id: WorkflowId,
    scope?: WorkflowScope,
    letters?: LetterChoice[],
    topics?: ResearchTopic[]
  ) => void;
  /** All eight in order; `startOver` runs every step, done or not. */
  runEverything: (startOver?: boolean) => void;
  runRemaining: () => void;
  /** Drop the steps not started yet; the running one finishes. */
  cancelQueued: () => void;
  /** Drop the queued steps and stop the running ones. */
  stop: () => void;
  /** Open a step's thread to answer it; its open questions count as seen. */
  answer: (id: WorkflowId, threadId: string) => void;
  registerExecutor: (executor: WorkflowExecutor) => () => void;
};

export const WorkflowsContext = createContext<WorkflowsState | null>(null);

export type PendingWorkflow = {
  id: WorkflowId;
  /** "Organize Into Folders · all 12 files", as the sent message reads. */
  line: string;
};

/** A step whose prompt waits for the workflow thread to be free. */
const WAITING = "Waiting for Casey";
const WAITING_RESEARCH = "Waiting for Case Research to finish";

/**
 * Letters wait for the research they draw on: the research refetched after
 * its turn (time for its runs to show as running), then every few seconds,
 * up to the drafting run's own 20-minute wait for analyses.
 */
const RESEARCH_SETTLE_MS = 1500;
const RESEARCH_POLL_MS = 5000;
const RESEARCH_WAIT_MS = 20 * 60_000;
/**
 * After a full retitle, the tree refetches with the new titles; a change
 * within this long is that refetch, not someone renaming a file.
 */
const COVER_SETTLE_MS = 10_000;

const WRITTEN = new Set<WorkspaceStatus>(["ready", "stale"]);

type QueueItem = {
  id: WorkflowId;
  scope: WorkflowScope;
  chained: boolean;
  /** A chained step may sit out when its work is done (not on a start-over). */
  skippable: boolean;
  /** Draft Letters' letters; the firm defaults when absent. */
  letters?: LetterChoice[];
  /** Fill Forms' copies; the current picks when absent. */
  forms?: FormChoice[];
  /** Case Research's topics; the default picks when absent. */
  topics?: ResearchTopic[];
};

/** How a step of the current run ended; `blocked` sat out for a failed one. */
type Ended = "done" | "failed" | "skipped" | "blocked";

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/** Research the case is still running in the background. */
const researchRunning = (
  state: ResearchTopicsState,
  facts: WorkflowFacts
): boolean =>
  state.kind === "ready"
    ? state.topics.some((t) => t.status === "running")
    : facts.research.some((i) => i.status === "running");

/** Fill Forms' catalog and the copies the case starts with. */
const useFormTypes = (individualId: string, visa: string) => {
  const [types, setTypes] = useState<FormTypes | null>(null);
  useEffect(() => {
    setTypes(null);
    let cancelled = false;
    WorkspaceTreeAPI.getFormTypes(individualId)
      .then((r) => {
        if (!cancelled) setTypes(formTypesOf(r));
      })
      .catch(() => {
        if (!cancelled) setTypes({ catalog: [], picks: null });
      });
    return () => {
      cancelled = true;
    };
  }, [individualId, visa]);
  return types;
};

/** The case's letter types for its visa, as New Draft lists them. */
const useLetterTypes = (individualId: string, visa: string) => {
  const [types, setTypes] = useState<LetterType[] | null>(null);
  useEffect(() => {
    setTypes(null);
    let cancelled = false;
    WorkspaceTreeAPI.getDraftTypes(individualId)
      .then((r) => {
        if (!cancelled) setTypes(letterTypesOf(r.draft_types));
      })
      .catch(() => {
        if (!cancelled) setTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [individualId, visa]);
  return types;
};

/**
 * The case still has CaseBlink's default style while the firm saved
 * another: a run applies the firm's. The tree always sends a scheme, so a
 * case that chose the default on purpose reads the same.
 */
const styleUnsettled = (
  scheme: ExhibitScheme,
  firm: ExhibitScheme | null
): boolean =>
  Boolean(firm) &&
  matchPreset(scheme)?.id === matchPreset(DEFAULT_EXHIBIT_SCHEME)?.id &&
  caseStyleDifference(scheme, firm) !== null;

const useFirm = () => {
  const lawyer = useSelector(lawyerSelectors.selectAll) as {
    law_firm_id?: string | null;
  } | null;
  const lawFirmId = lawyer?.law_firm_id || null;
  const [firm, setFirm] = useState<LawFirm | null>(null);
  const [library, setLibrary] = useState<FirmLibraryEntry[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onUpdated = () => setTick((n) => n + 1);
    window.addEventListener(FIRM_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(FIRM_UPDATED_EVENT, onUpdated);
  }, []);

  useEffect(() => {
    if (!lawFirmId) {
      setFirm(null);
      setLibrary([]);
      return undefined;
    }
    let cancelled = false;
    LawFirmsAPI.getById(lawFirmId)
      .then((f) => {
        if (!cancelled) setFirm(f);
      })
      .catch(() => undefined);
    firmKnowledgeApi
      .listLibrary({ lawFirmId }, "active")
      .then((data) => {
        if (!cancelled) setLibrary(data.entries);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [lawFirmId, tick]);

  return { firm, library, lawFirmId };
};

export const WorkflowsProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const { individualId, invalidate } = useCaseIde();
  const { fs, workspace } = useCaseData();
  const researchTopics = useResearchTopics(
    individualId,
    workspace.drafts,
    fs.nodes
  );
  const visa = useCaseVisa();
  const { firm, library, lawFirmId } = useFirm();
  const caseRecord = useCaseOptional()?.case;
  const caseType = useMemo(
    () => liveTypeOf(caseCustomType(caseRecord), firm?.custom_case_types ?? []),
    [caseRecord, firm]
  );
  const draftingVisa = draftingVisaOf(caseType, visa || "");
  const templates = useMemo(
    () =>
      typeDefaultTemplates(
        caseType,
        library,
        defaultTemplatesFor(library, draftingVisa)
      ),
    [caseType, library, draftingVisa]
  );

  const settings = useMemo(
    () => workflowSettings(firm, draftingVisa, fs.scheme, templates),
    [firm, draftingVisa, fs.scheme, templates]
  );
  const firmStyle = useMemo(
    () => firmScheme(firm?.default_exhibit_scheme),
    [firm]
  );
  const caseLabelStyle = useMemo(
    () => caseStyleDifference(fs.scheme, firmStyle),
    [fs.scheme, firmStyle]
  );
  const letterTypes = useLetterTypes(individualId, visa || "");
  const [pickOverrides, setPickOverrides] = useState<LetterPicks>({});
  const letterPicks = useMemo(
    () => ({
      ...defaultPicks(letterTypes ?? [], library, draftingVisa, templates),
      ...(pickOverrides as Record<string, LetterPick>)
    }),
    [letterTypes, library, draftingVisa, templates, pickOverrides]
  );
  const letters = useMemo(
    () =>
      lettersFor(
        letterTypes ?? [],
        library,
        draftingVisa,
        letterPicks,
        templates
      ),
    [letterTypes, library, draftingVisa, letterPicks, templates]
  );
  const formTypes = useFormTypes(individualId, visa || "");
  const [formOverrides, setFormOverrides] = useState<FormPick[] | null>(null);
  const formPicks = formOverrides ?? formTypes?.picks ?? null;
  const forms = useMemo(
    () =>
      formChoicesOf(
        formPicks ?? [],
        workspace.forms.map((f) => ({
          id: f.case_extend_uscis_filled_form_id,
          formNumber: f.form_number,
          label: f.label ?? null
        }))
      ),
    [formPicks, workspace.forms]
  );
  const editForms = useCallback(
    (edit: (picks: FormPick[]) => FormPick[]) =>
      setFormOverrides((cur) => edit(cur ?? formTypes?.picks ?? [])),
    [formTypes]
  );
  const addForm = useCallback(
    (form: FormOption) => editForms((picks) => addFormPick(picks, form)),
    [editForms]
  );
  const removeForm = useCallback(
    (id: string) => editForms((picks) => removeFormPick(picks, id)),
    [editForms]
  );
  const setFormNote = useCallback(
    (id: string, note: string) =>
      editForms((picks) => setFormPickNote(picks, id, note)),
    [editForms]
  );
  const [packetPicks, setPacketPickState] = useState<Record<string, boolean>>(
    {}
  );
  const setPacketPick = useCallback(
    (key: string, on: boolean) =>
      setPacketPickState((cur) => ({ ...cur, [key]: on })),
    []
  );
  const [packetOrder, setPacketOrder] = useState<string[]>([]);
  const facts = useMemo(
    () =>
      workflowFacts(fs.nodes, workspace.sections, {
        labelStyle: labelStyleOf(fs.scheme) ?? "",
        styleSettled: !styleUnsettled(fs.scheme, firmStyle),
        researchTopics,
        titleSettings: titleSettingsKey(settings),
        letterTypes: letterTypes ? letters : null,
        packetPicks,
        packetOrder,
        packetContents: packetContentsOf(settings.packetSettings),
        packetKindOrder: packetOrderOf(settings.packetSettings)
      }),
    [
      fs.nodes,
      workspace.sections,
      fs.scheme,
      firmStyle,
      researchTopics,
      settings,
      letterTypes,
      letters,
      packetPicks,
      packetOrder,
      settings.packetSettings
    ]
  );

  const setLetterPick = useCallback(
    (draftType: string, pick: LetterPick) =>
      setPickOverrides((cur) => ({ ...cur, [draftType]: pick })),
    []
  );

  const [topicPicks, setTopicPickState] = useState<TopicPicks>({});
  const setTopicPicks = useCallback(
    (ids: string[], picked: boolean) =>
      setTopicPickState((cur) => ({
        ...cur,
        ...Object.fromEntries(ids.map((id) => [id, picked]))
      })),
    []
  );
  const topics = useMemo(
    () =>
      researchTopics.kind === "ready"
        ? pickedTopics(researchTopics, topicPicks)
        : [],
    [researchTopics, topicPicks]
  );
  const defaultTopics = useMemo(
    () => (researchTopics.kind === "ready" ? pickedTopics(researchTopics) : []),
    [researchTopics]
  );

  const [records, setRecords] = useState(() => readRuns(individualId));
  const [notes, setNotes] = useState(() => readNotes(individualId));
  const [loadedFor, setLoadedFor] = useState(individualId);
  if (loadedFor !== individualId) {
    setLoadedFor(individualId);
    setRecords(readRuns(individualId));
    setNotes(readNotes(individualId));
    setPickOverrides({});
    setFormOverrides(null);
    setTopicPickState({});
    setPacketPickState({});
    setPacketOrder([]);
  }
  const [steps, setSteps] = useState<
    Partial<Record<WorkflowId, WorkflowStepState>>
  >({});
  const [runIds, setRunIds] = useState<WorkflowId[]>([]);
  const [activeIds, setActiveIds] = useState<WorkflowId[]>([]);
  /** Letters already written; an empty or failed draft has no letter yet. */
  const draftTypes = useMemo(
    () =>
      new Set(
        workspace.drafts
          .filter(
            (d) =>
              !(d.metadata as { deleted?: boolean } | undefined)?.deleted &&
              WRITTEN.has(draftStatus(d))
          )
          .map((d) => String(d.draft_type))
      ),
    [workspace.drafts]
  );

  const views = useMemo(
    () => workflowViews(facts, records, steps),
    [facts, records, steps]
  );

  // The runner reads the latest of everything at the moment it sends a step.
  const defaultLetters = useMemo(
    () => lettersFor(letterTypes ?? [], library, draftingVisa, {}, templates),
    [letterTypes, library, draftingVisa, templates]
  );
  const now = {
    settings,
    facts,
    records,
    notes,
    caseLabelStyle,
    individualId,
    defaultLetters,
    forms: formPicks === null ? undefined : forms,
    draftTypes,
    researchTopics,
    defaultTopics,
    packetPicks,
    packetContents: packetContentsOf(settings.packetSettings),
    firmStyle,
    saveScheme: fs.saveScheme
  };
  const latest = useRef(now);
  latest.current = now;
  const executorRef = useRef<WorkflowExecutor | null>(null);
  const [executorTick, setExecutorTick] = useState(0);
  const queueRef = useRef<QueueItem[]>([]);
  /** The steps started and not ended, with the abort that stops each. */
  const activeRef = useRef(new Map<WorkflowId, AbortController>());
  /** Started steps, including whether the prompt is already on the thread. */
  const activeMetaRef = useRef(
    new Map<WorkflowId, QueueItem & { sent: boolean; threadId?: string }>()
  );
  /** This provider instance is the one on screen. A replaced one must not send. */
  const aliveRef = useRef(true);
  /** Stop cleared the run; a late finish must not write it back. */
  const stoppedRef = useRef(false);
  /** The stored run was read once for this case, after the chat could send. */
  const restoredRef = useRef(false);
  const seenCaseRef = useRef(individualId);
  const lanesRef = useRef(new Set<WorkflowLane>());
  /** How each step of the current run ended. */
  const endedRef = useRef(new Map<WorkflowId, Ended>());
  /** Steps of the current run that did work. */
  const workedRef = useRef(new Set<WorkflowId>());
  /** Letters wait while the research this run started is still running. */
  const researchGateRef = useRef(false);
  const linesRef = useRef(new Map<WorkflowId, string>());
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const runOrderRef = useRef<WorkflowId[]>([]);
  if (seenCaseRef.current !== individualId) {
    seenCaseRef.current = individualId;
    restoredRef.current = false;
    stoppedRef.current = false;
    queueRef.current = [];
    activeRef.current.clear();
    activeMetaRef.current.clear();
    lanesRef.current.clear();
    endedRef.current.clear();
    workedRef.current.clear();
    researchGateRef.current = false;
    runOrderRef.current = [];
    setRunIds([]);
    setSteps({});
    setActiveIds([]);
  }
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  /** Until when the tree refetched after a full retitle still updates its `covered`. */
  const coverUntilRef = useRef(0);
  const { allFiles } = facts;
  useEffect(() => {
    if (Date.now() > coverUntilRef.current) return;
    setRecords((cur) => {
      const last = cur.retitle;
      const titles = titlesKey(allFiles);
      if (!last?.covered || last.covered.titles === titles) return cur;
      const out = {
        ...cur,
        retitle: { ...last, covered: { ...last.covered, titles } }
      };
      writeRuns(latest.current.individualId, out);
      return out;
    });
  }, [allFiles]);
  const syncActive = useCallback(() => {
    const on = activeRef.current;
    setActiveIds(runOrderRef.current.filter((id) => on.has(id)));
  }, []);

  const promptFor = useCallback(
    (
      id: WorkflowId,
      scope: WorkflowScope = { kind: "default" },
      chained = false,
      letters?: LetterChoice[],
      topics?: ResearchTopic[],
      forms?: FormChoice[]
    ) => {
      const l = latest.current;
      const research = l.researchTopics;
      return workflowById(id).prompt({
        settings: l.settings,
        scope,
        caseLabelStyle: l.caseLabelStyle,
        files: l.facts.files,
        unsorted: l.facts.unsorted,
        allFiles: l.facts.allFiles,
        untitled: l.facts.untitled,
        note: l.notes[id] ?? "",
        chained,
        letters: letters ?? l.defaultLetters,
        forms: forms ?? l.forms,
        research:
          research.kind === "ready"
            ? {
                posture: research.posture,
                topics: topics ?? l.defaultTopics
              }
            : null,
        packet: packetArgsOf(
          l.facts.packetParts,
          l.packetPicks,
          l.packetContents
        )
      });
    },
    []
  );

  const record = useCallback(
    (id: WorkflowId, next: WorkflowRunRecord) =>
      setRecords((cur) => {
        const out = { ...cur, [id]: next };
        writeRuns(latest.current.individualId, out);
        return out;
      }),
    []
  );

  const setStep = useCallback(
    (id: WorkflowId, step: WorkflowStepState | null) =>
      setSteps((cur) => {
        const out = { ...cur };
        if (step) out[id] = step;
        else delete out[id];
        return out;
      }),
    []
  );

  const storedOf = (item: QueueItem): StoredQueueItem => ({
    id: item.id,
    scope: item.scope,
    chained: item.chained,
    skippable: item.skippable,
    ...(item.letters ? { letters: item.letters } : {}),
    ...(item.forms ? { forms: item.forms } : {}),
    ...(item.topics ? { topics: item.topics } : {})
  });

  const queueItemOf = (item: StoredQueueItem): QueueItem => ({
    id: item.id,
    scope: item.scope,
    chained: item.chained,
    skippable: item.skippable,
    ...(item.letters ? { letters: item.letters } : {}),
    ...(item.forms ? { forms: item.forms } : {}),
    ...(item.topics ? { topics: item.topics } : {})
  });

  /** Remember the steps still to run. A dead instance does not overwrite them. */
  const persist = useCallback(() => {
    if (!aliveRef.current || stoppedRef.current) return;
    const id = latest.current.individualId;
    const items: StoredQueueItem[] = [
      ...[...activeMetaRef.current.values()].map((item) => ({
        ...storedOf(item),
        started: true,
        ...(item.sent ? { sent: true } : {}),
        ...(item.threadId ? { threadId: item.threadId } : {})
      })),
      ...queueRef.current.map((item) => storedOf(item))
    ];
    if (!items.length) {
      clearQueue(id);
      return;
    }
    writeQueue(id, {
      items,
      ended: Object.fromEntries(endedRef.current) as StoredQueue["ended"],
      worked: [...workedRef.current],
      order: runOrderRef.current,
      researchGate: researchGateRef.current
    });
  }, []);

  /**
   * The in-memory queue was dropped (a refresh remounts this provider).
   * Put back the steps storage still has, in their order, skipping any
   * this instance already finished or is following.
   */
  const adoptStoredQueue = useCallback(() => {
    if (stoppedRef.current) return;
    const stored = readQueue(latest.current.individualId);
    if (!stored) return;
    for (const [id, status] of Object.entries(stored.ended))
      if (!endedRef.current.has(id as WorkflowId) && status)
        endedRef.current.set(id as WorkflowId, status);
    for (const id of stored.worked)
      if (!workedRef.current.has(id)) workedRef.current.add(id);
    if (stored.researchGate) researchGateRef.current = true;
    if (!runOrderRef.current.length && stored.order.length) {
      runOrderRef.current = stored.order;
      setRunIds(stored.order);
    }
    const pending = stored.items.filter(
      (raw) =>
        !raw.started &&
        !raw.sent &&
        !endedRef.current.has(raw.id) &&
        !activeRef.current.has(raw.id) &&
        !activeMetaRef.current.has(raw.id)
    );
    if (!pending.length) return;
    const pendingIds = new Set(pending.map((raw) => raw.id));
    queueRef.current = [
      ...pending.map(queueItemOf),
      ...queueRef.current.filter((item) => !pendingIds.has(item.id))
    ];
  }, []);

  /** Exhibit numbering in a run: apply the firm's style if the case has none. */
  const applyStyle = useCallback(async (): Promise<WorkflowTurnOutcome> => {
    const l = latest.current;
    if (l.facts.styleSettled || !l.firmStyle) return { status: "done" };
    try {
      await l.saveScheme(l.firmStyle);
      return { status: "done" };
    } catch (e) {
      return {
        status: "failed",
        detail: e instanceof Error ? e.message : "The style didn't save"
      };
    }
  }, []);

  /** Send one step's prompt and wait for its turn to end. */
  const runStep = useCallback(
    async (
      item: QueueItem,
      abort: AbortController,
      /** The prompt was already sent; follow that turn instead of sending again. */
      follow = false
    ): Promise<WorkflowTurnOutcome> => {
      const def = workflowById(item.id);
      if (def.kind === "direct") return applyStyle();
      const executor = executorRef.current;
      if (!executor) return { status: "failed", detail: "The chat isn't open" };
      const request = {
        workflowId: item.id,
        text: promptFor(
          item.id,
          item.scope,
          item.chained,
          item.letters,
          item.topics,
          item.forms
        ),
        skillId: def.skillId,
        lane: def.lane,
        stamp: {
          id: item.id,
          template: def.template,
          version: def.version,
          chained: item.chained
        }
      };
      const onPhase = (
        phase: "waiting" | "running" | "needs-you",
        threadId?: string,
        hold?: WorkflowStepState["hold"],
        detail?: string
      ) => {
        if (!aliveRef.current) return;
        if ((phase === "running" || phase === "needs-you") && threadId) {
          const meta = activeMetaRef.current.get(item.id);
          if (meta) {
            meta.sent = true;
            meta.threadId = threadId;
            persist();
          }
        }
        setStep(item.id, {
          ...(phase === "waiting"
            ? { status: "queued", detail: WAITING }
            : { status: phase, ...(detail ? { detail } : {}) }),
          ...(threadId ? { threadId } : {}),
          ...(hold ? { hold } : {})
        });
      };
      try {
        const meta = activeMetaRef.current.get(item.id);
        if (follow && executor.resume)
          return await executor.resume(
            request,
            meta?.threadId,
            onPhase,
            abort.signal
          );
        return await executor.run(request, onPhase, abort.signal);
      } catch (e) {
        return {
          status: "failed",
          detail: e instanceof Error ? e.message : "The run failed"
        };
      }
    },
    [applyStyle, persist, promptFor, setStep]
  );

  const endRunIfIdle = useCallback(() => {
    if (queueRef.current.length || activeRef.current.size) return;
    endedRef.current.clear();
    workedRef.current.clear();
    researchGateRef.current = false;
    runOrderRef.current = [];
    setRunIds([]);
    syncActive();
    if (aliveRef.current) clearQueue(latest.current.individualId);
  }, [syncActive]);

  // `schedule` and `start` call each other; the ref breaks the cycle.
  const scheduleRef = useRef<() => void>(() => undefined);

  /** Letters wait for the research this run started, up to RESEARCH_WAIT_MS. */
  const holdForResearch = useCallback(async () => {
    researchGateRef.current = true;
    persist();
    const until = Date.now() + RESEARCH_WAIT_MS;
    for (let first = true; researchGateRef.current && Date.now() < until; ) {
      if (!aliveRef.current) return;
      invalidate(["research"]);
      // eslint-disable-next-line no-await-in-loop
      await sleep(first ? RESEARCH_SETTLE_MS : RESEARCH_POLL_MS);
      first = false;
      if (!aliveRef.current || !researchGateRef.current) return;
      const l = latest.current;
      if (!researchRunning(l.researchTopics, l.facts)) break;
    }
    if (!aliveRef.current) return;
    if (researchGateRef.current && Date.now() >= until)
      toast.info(
        "Case Research is still running, so the letters start without its last results."
      );
    researchGateRef.current = false;
    persist();
    scheduleRef.current();
  }, [invalidate, persist]);

  const start = useCallback(
    async (
      item: QueueItem,
      /** Set when this step already started before the page remounted. */
      follow: { sent: boolean; threadId?: string } | null = null
    ) => {
      if (!aliveRef.current || stoppedRef.current) return;
      if (activeRef.current.has(item.id)) return;
      const def = workflowById(item.id);
      const abort = new AbortController();
      activeRef.current.set(item.id, abort);
      activeMetaRef.current.set(item.id, {
        ...item,
        sent: Boolean(follow?.sent) || def.kind === "direct",
        ...(follow?.threadId ? { threadId: follow.threadId } : {})
      });
      lanesRef.current.add(def.lane);
      syncActive();
      persist();
      const titlesBefore = titlesKey(latest.current.facts.allFiles);
      setStep(
        item.id,
        def.kind === "direct" || follow?.sent
          ? {
              status: "running",
              ...(follow?.threadId ? { threadId: follow.threadId } : {})
            }
          : { status: "queued", detail: WAITING }
      );
      const outcome = await runStep(item, abort, follow !== null);
      activeRef.current.delete(item.id);
      activeMetaRef.current.delete(item.id);
      lanesRef.current.delete(def.lane);
      if (!aliveRef.current) return;
      if (stoppedRef.current) {
        syncActive();
        setStep(item.id, null);
        endRunIfIdle();
        return;
      }
      // The queue may have been dropped with this instance. Read it back
      // before this step is forgotten, so the next one still goes out.
      adoptStoredQueue();
      syncActive();
      if (outcome.status === "cancelled") {
        setStep(item.id, null);
        runOrderRef.current = runOrderRef.current.filter((x) => x !== item.id);
        setRunIds((cur) => cur.filter((x) => x !== item.id));
      } else {
        endedRef.current.set(item.id, outcome.status);
        if (outcome.status === "done") workedRef.current.add(item.id);
        const l = latest.current;
        const covered =
          item.id === "retitle" &&
          outcome.status === "done" &&
          item.scope.kind === "default";
        if (covered) coverUntilRef.current = Date.now() + COVER_SETTLE_MS;
        record(item.id, {
          ...(covered
            ? {
                covered: {
                  settings: l.facts.titleSettings,
                  titles: titlesKey(l.facts.allFiles),
                  before: titlesBefore
                }
              }
            : {}),
          status: outcome.status,
          at: new Date().toISOString(),
          ...(outcome.detail ? { detail: outcome.detail } : {}),
          ...(outcome.model ? { model: outcome.model } : {}),
          ...(outcome.questions?.length
            ? { questions: outcome.questions }
            : {}),
          ...(outcome.threadId ? { threadId: outcome.threadId } : {})
        });
        setStep(item.id, null);
        invalidate(["fs", "drafts", "forms", "packet", "research"]);
        const lettersWait = queueRef.current.some((q) => q.id === "letters");
        if (item.id === "research" && outcome.status === "done" && lettersWait)
          void holdForResearch();
      }
      persist();
      scheduleRef.current();
    },
    [
      adoptStoredQueue,
      endRunIfIdle,
      holdForResearch,
      invalidate,
      persist,
      record,
      runStep,
      setStep,
      syncActive
    ]
  );

  /**
   * Start every queued step that can start now: the steps it comes after
   * have ended, its lane is free and no earlier step waits for that lane.
   * A step a failed one blocks, or one with nothing to do, ends at once.
   */
  const schedule = useCallback(() => {
    adoptStoredQueue();
    const ended = endedRef.current;
    const pending = (d: WorkflowId) =>
      queueRef.current.some((q) => q.id === d) || activeRef.current.has(d);
    const take = (item: QueueItem) => {
      queueRef.current = queueRef.current.filter((q) => q !== item);
    };
    let progressed = true;
    while (progressed) {
      progressed = false;
      for (const item of [...queueRef.current]) {
        const def = workflowById(item.id);
        const at = queueRef.current.indexOf(item);
        const blocker = def.dependsOn.find((d) => {
          const e = ended.get(d);
          return e === "failed" || e === "blocked";
        });
        if (blocker) {
          take(item);
          progressed = true;
          ended.set(item.id, "blocked");
          setStep(item.id, null);
          record(item.id, {
            status: "skipped",
            at: new Date().toISOString(),
            detail: `Skipped: ${workflowById(blocker).title} didn't finish`
          });
          // eslint-disable-next-line no-continue
          continue;
        }
        const waits =
          [...def.after, ...def.dependsOn].some(pending) ||
          lanesRef.current.has(def.lane) ||
          queueRef.current
            .slice(0, at)
            .some((q) => workflowById(q.id).lane === def.lane);
        if (waits) {
          // eslint-disable-next-line no-continue
          continue;
        }
        if (item.id === "letters" && researchGateRef.current) {
          if (stepsRef.current.letters?.detail !== WAITING_RESEARCH)
            setStep("letters", { status: "queued", detail: WAITING_RESEARCH });
          // eslint-disable-next-line no-continue
          continue;
        }
        const l = latest.current;
        const sitOut = skipReason(item.id, {
          facts: l.facts,
          records: l.records,
          scope: item.scope,
          chained: item.chained && item.skippable,
          worked: workedRef.current,
          letters: item.letters ?? l.defaultLetters,
          forms: item.forms ?? l.forms,
          draftTypes: l.draftTypes,
          research: l.researchTopics,
          topics: item.topics ?? l.defaultTopics
        });
        take(item);
        progressed = true;
        if (sitOut) {
          ended.set(item.id, "skipped");
          setStep(item.id, null);
          const prev = l.records[item.id];
          if (sitOut.upToDate && prev?.status === "done") {
            const kept: WorkflowRunRecord = {
              ...prev,
              detail: sitOut.detail,
              upToDate: true
            };
            delete kept.questions;
            record(item.id, kept);
          } else
            record(item.id, {
              status: "skipped",
              at: new Date().toISOString(),
              detail: sitOut.detail,
              ...(sitOut.upToDate ? { upToDate: true } : {})
            });
        } else void start(item);
      }
    }
    persist();
    endRunIfIdle();
  }, [adoptStoredQueue, endRunIfIdle, persist, record, setStep, start]);
  scheduleRef.current = schedule;

  const enqueue = useCallback(
    (items: QueueItem[]) => {
      stoppedRef.current = false;
      const inRun = new Set([
        ...queueRef.current.map((q) => q.id),
        ...activeRef.current.keys(),
        ...activeMetaRef.current.keys()
      ]);
      const picked = latest.current.forms;
      // A run fills the copies picked when it started, a refresh too.
      const fresh = items
        .filter((i) => !inRun.has(i.id))
        .map((i) =>
          i.id === "forms" && !i.forms && picked?.length
            ? { ...i, forms: picked }
            : i
        );
      if (!fresh.length) {
        toast.info("That workflow is already in this run.");
        return;
      }
      // Main-lane steps run in the open chat; show it once, as the run starts.
      if (
        fresh.some((i) => {
          const def = workflowById(i.id);
          return def.kind === "casey" && def.lane === "main";
        })
      )
        ideLayoutStore.show("right");
      queueRef.current.push(...fresh);
      for (const i of fresh)
        linesRef.current.set(
          i.id,
          readWorkflowPrompt(
            promptFor(i.id, i.scope, i.chained, i.letters, i.topics, i.forms)
          )?.line ?? workflowById(i.id).title
        );
      runOrderRef.current = [...runOrderRef.current, ...fresh.map((i) => i.id)];
      setRunIds(runOrderRef.current);
      setSteps((cur) => {
        const out = { ...cur };
        for (const i of fresh) out[i.id] = { status: "queued" };
        return out;
      });
      const queued: WorkflowStepState = { status: "queued" };
      stepsRef.current = {
        ...stepsRef.current,
        ...Object.fromEntries(fresh.map((i) => [i.id, queued]))
      };
      persist();
      schedule();
    },
    [persist, promptFor, schedule]
  );

  const run = useCallback(
    (
      id: WorkflowId,
      scope: WorkflowScope = { kind: "default" },
      letters?: LetterChoice[],
      topics?: ResearchTopic[]
    ) =>
      enqueue([
        { id, scope, chained: false, skippable: false, letters, topics }
      ]),
    [enqueue]
  );

  const runEverything = useCallback(
    (startOver = false) =>
      enqueue(
        REQUIRED_WORKFLOW_IDS.map((id) => ({
          id,
          scope: { kind: "default" },
          chained: true,
          skippable: !startOver
        }))
      ),
    [enqueue]
  );

  const remaining = useMemo(() => remainingIds(views), [views]);
  const runRemaining = useCallback(
    () =>
      enqueue(
        remaining.map((id) => ({
          id,
          scope: { kind: "default" },
          chained: true,
          skippable: true
        }))
      ),
    [enqueue, remaining]
  );

  const drop = useCallback((ids: WorkflowId[]) => {
    if (!ids.length) return;
    runOrderRef.current = runOrderRef.current.filter((x) => !ids.includes(x));
    setRunIds(runOrderRef.current);
    setSteps((cur) => {
      const out = { ...cur };
      for (const id of ids) delete out[id];
      return out;
    });
  }, []);

  const cancelQueued = useCallback(() => {
    const dropped = queueRef.current.map((q) => q.id);
    queueRef.current = [];
    researchGateRef.current = false;
    drop(dropped);
    persist();
    endRunIfIdle();
  }, [drop, endRunIfIdle, persist]);

  const dequeue = useCallback(
    (id: WorkflowId) => {
      if (stepsRef.current[id]?.status !== "queued") return;
      const started = activeRef.current.get(id);
      if (started) {
        // The executor sees the abort before it sends and reports `cancelled`.
        started.abort();
        return;
      }
      queueRef.current = queueRef.current.filter((q) => q.id !== id);
      drop([id]);
      persist();
      // A step that waited on this one may start now.
      schedule();
    },
    [drop, persist, schedule]
  );

  // The chat lists the main lane's steps not sent yet, on that lane's thread.
  const pending = useMemo<PendingWorkflow[]>(
    () =>
      runIds
        .filter((id) => {
          const def = workflowById(id);
          return (
            steps[id]?.status === "queued" &&
            def.kind === "casey" &&
            def.lane === "main"
          );
        })
        .map((id) => ({
          id,
          line: linesRef.current.get(id) ?? workflowById(id).title
        })),
    [runIds, steps]
  );

  const stop = useCallback(() => {
    stoppedRef.current = true;
    clearQueue(latest.current.individualId);
    cancelQueued();
    for (const abort of activeRef.current.values()) abort.abort();
  }, [cancelQueued]);

  const answer = useCallback((id: WorkflowId, threadId: string) => {
    ideLayoutStore.show("right");
    executorRef.current?.open(threadId);
    setRecords((cur) => {
      const last = cur[id];
      if (!last?.questions) return cur;
      const seen = { ...last };
      delete seen.questions;
      const out = { ...cur, [id]: seen };
      writeRuns(latest.current.individualId, out);
      return out;
    });
  }, []);

  const setNote = useCallback((id: WorkflowId, note: string) => {
    setNotes((cur) => {
      const out = { ...cur };
      if (note.trim()) out[id] = note;
      else delete out[id];
      writeNotes(latest.current.individualId, out);
      return out;
    });
  }, []);

  const registerExecutor = useCallback((executor: WorkflowExecutor) => {
    executorRef.current = executor;
    setExecutorTick((n) => n + 1);
    return () => {
      if (executorRef.current === executor) executorRef.current = null;
    };
  }, []);

  const restoreRef = useRef<(stored: StoredQueue) => void>(() => undefined);
  restoreRef.current = (stored) => {
    if (stoppedRef.current) return;
    endedRef.current = new Map(
      Object.entries(stored.ended).flatMap(([id, status]) =>
        status ? [[id as WorkflowId, status] as const] : []
      )
    );
    workedRef.current = new Set(stored.worked);
    researchGateRef.current = stored.researchGate;
    runOrderRef.current = stored.order.length
      ? stored.order
      : stored.items.map((item) => item.id);
    setRunIds(runOrderRef.current);
    const pending: QueueItem[] = [];
    const inflight: { item: QueueItem; sent: boolean; threadId?: string }[] =
      [];
    for (const raw of stored.items.filter(
      (item) => !endedRef.current.has(item.id)
    )) {
      const item = queueItemOf(raw);
      if (raw.started || raw.sent)
        inflight.push({
          item,
          sent: Boolean(raw.sent),
          ...(raw.threadId ? { threadId: raw.threadId } : {})
        });
      else pending.push(item);
    }
    queueRef.current = pending;
    setSteps((cur) => {
      const out = { ...cur };
      for (const item of pending) out[item.id] = { status: "queued" };
      for (const step of inflight)
        out[step.item.id] = step.sent
          ? {
              status: "running",
              ...(step.threadId ? { threadId: step.threadId } : {})
            }
          : { status: "queued", detail: WAITING };
      return out;
    });
    for (const step of inflight)
      void start(step.item, {
        sent: step.sent,
        ...(step.threadId ? { threadId: step.threadId } : {})
      });
    if (stored.researchGate) void holdForResearch();
    schedule();
  };

  useEffect(() => {
    if (restoredRef.current) return;
    if (!executorRef.current) return;
    if (lawFirmId && !firm) return;
    if (letterTypes === null) return;
    restoredRef.current = true;
    const stored = readQueue(individualId);
    if (!stored?.items.length) return;
    restoreRef.current(stored);
  }, [executorTick, firm, individualId, lawFirmId, letterTypes]);

  const value = useMemo<WorkflowsState>(
    () => ({
      settings,
      firmScheme: firmStyle,
      caseLabelStyle,
      facts,
      views,
      nextUp: nextUpId(views),
      letterTypes,
      library,
      letterPicks,
      setLetterPick,
      letters,
      formCatalog: formTypes?.catalog ?? null,
      formPicks,
      addForm,
      removeForm,
      setFormNote,
      forms,
      packetPicks,
      setPacketPick,
      setPacketOrder,
      researchTopics,
      topicPicks,
      setTopicPicks,
      topics,
      remaining,
      done: doneCount(views),
      priorWork: hasPriorWork(facts, records),
      running: activeIds.length > 0 || runIds.length > 0,
      current: activeIds[0] ?? null,
      active: activeIds,
      runIds,
      pending,
      dequeue,
      notes,
      setNote,
      promptFor: (id, scope, picked, topicList) =>
        promptFor(id, scope, false, picked, topicList),
      run,
      runEverything,
      runRemaining,
      cancelQueued,
      stop,
      answer,
      registerExecutor
    }),
    [
      stop,
      answer,
      letterTypes,
      library,
      letterPicks,
      setLetterPick,
      letters,
      formTypes,
      formPicks,
      addForm,
      removeForm,
      setFormNote,
      forms,
      packetPicks,
      setPacketPick,
      setPacketOrder,
      researchTopics,
      topicPicks,
      setTopicPicks,
      topics,
      settings,
      firmStyle,
      caseLabelStyle,
      facts,
      views,
      remaining,
      records,
      activeIds,
      runIds,
      pending,
      dequeue,
      notes,
      setNote,
      promptFor,
      run,
      runEverything,
      runRemaining,
      cancelQueued,
      registerExecutor
    ]
  );

  return (
    <WorkflowsContext.Provider value={value}>
      {children}
    </WorkflowsContext.Provider>
  );
};

export const useWorkflowsOptional = (): WorkflowsState | null =>
  useContext(WorkflowsContext);

export const useWorkflows = (): WorkflowsState => {
  const ctx = useContext(WorkflowsContext);
  if (!ctx)
    throw new Error("useWorkflows must be used within WorkflowsProvider");
  return ctx;
};
